import React, { useEffect, useState, useCallback } from 'react';
import { Grid, Box, Paper, TableContainer, LinearProgress, Typography, Select, MenuItem, FormControl, InputLabel } from "@mui/material";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import { Header } from "../../../components/global/Header";
import FormListToolbar from "../../../components/global/Toolbar";
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { useNavigate } from "react-router";
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import SearchField from '../../../components/search/Search';
import { useCategoryType } from './useCategoryType';
import ErrorNotification from '../../../components/ErrorNotification/ErrorNotification';
import api from "../../../services/api";
import { useNotifier } from "muinotify";
import { exportToExcel } from '../../../components/export/exportToExcel';
import { exportToPdf } from '../../../components/export/exportToPdf';
import { exportToCsv } from '../../../components/export/exportToCsv';
import CategoryTypeDetails from './CategoryTypeDetails';
import useSWR from 'swr';

const CategoryType = () => {
  const { notifier } = useNotifier();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedProduction, setSelectedProduction] = useState(null);
  const { category_type, isLoading, error, mutate } = useCategoryType(searchQuery, selectedProduction?.id);
  const [isUpdating, setIsUpdating] = useState(false);
  const [selectedCategoryType, setSelectedCategoryType] = useState(null);
  const NAV_URL = `category_type`;
  const URL = `production-category-type`;

  const { data: productions } = useSWR('/productions');

  mutate();

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  function getRowId(row) {
    return row.id;
  }

  useEffect(() => {
    if (productions && productions.length > 0) {
      const savedProduction = localStorage.getItem('selectedProduction');
      if (savedProduction) {
        try {
          const parsedProduction = JSON.parse(savedProduction);
          const production = productions.find(p => p.id === parsedProduction.id);
          if (production) {
            setSelectedProduction(production);
          }
        } catch (e) {
          // Invalid saved production, ignore
        }
      }
    }
  }, [productions]);

  useEffect(() => {
    if (selectedProduction) {
      localStorage.setItem('selectedProduction', JSON.stringify(selectedProduction));
    }
  }, [selectedProduction]);

  const handleProductionChange = (event) => {
    const value = event.target.value;
    if (value === '') {
      setSelectedProduction(null);
    } else {
      const production = productions?.find((p) => p.id === value);
      setSelectedProduction(production || null);
    }
    mutate();
  };

  const handleClear = () => {
    setSearchQuery("");
    mutate();
  };

  let navigate = useNavigate();

  const handlePanelClose = () => {
    setSelectedCategoryType(null);
  };

  const handleView = (category_type) => {
    setSelectedCategoryType(category_type);
  };

  const handleEdit = (id) => {
    navigate(`/${NAV_URL}/update/${id}`);
  };

  const handleAdd = () => {
    navigate(`/${NAV_URL}/create/`);
  };

  const handleDelete = useCallback((id) => {
    confirmAlert({
      title: 'Επιβεβαίωση διαγραφής',
      message: 'Είστε βέβαιοι ότι θέλετε να διαγράψετε αυτήν την εγγραφή;',
      buttons: [
        {
          label: 'Ναι',
          onClick: async () => {
            try {
              await api.delete(`/${URL}/${id}`);
              notifier("Η εγγραφή διαγράφηκε με επιτυχία.", { variant: "success" });
              await mutate();
            } catch (err) {
              notifier("Αποτυχία διαγραφής εγγραφής. Σφάλμα: " + err.message, { variant: "error" });
            }
          }
        },
        {
          label: 'Όχι',
        }
      ]
    });
  }, [mutate, notifier]);


  const columnsToExtract = ['clientName', 'clientName', 'contactPerson', 'clientAddress', 'clientCity', 'clientPhone', 'clientEmail', 'clientNotes'];

  const handleExportToExcel = () => {
    exportToExcel(category_type || [], columns, columnsToExtract, 'clients-list', 'Clients List');
  };

  const handleExportToPdf = () => {
    exportToPdf(category_type || [], columns, columnsToExtract, 'clients-list');
  };

  const handleExportToCsv = () => {
    exportToCsv(category_type || [], columns, columnsToExtract, 'clients-list');
  };

  const flattenedData = Array.isArray(category_type)
    ? category_type.map((pruduction) => ({
      ...pruduction,
      pruduction: pruduction?.pc_pruduction?.p_descr || null,
    }))
    : [];

  const columns = [
    { field: 'pct_descr', headerName: 'Περιγραφή', flex: 1, headerAlign: 'center', },
    {
      field: 'pct_aa', headerName: 'A/A', flex: 1, headerAlign: 'center', renderCell: (params) => (
        <div
          style={{
            textAlign: 'center',
          }}
        >
          {params.value}
        </div>
      ),
    },
    {
      field: 'pruduction', headerName: 'Παραγωγή', flex: 1, headerAlign: 'center', renderCell: (params) => (
        <div
          style={{
            textAlign: 'center',
          }}
        >
          {params.value}
        </div>
      ),
    },
    {
      field: 'actions',
      type: 'actions',
      width: 100,
      headerName: "Actions",
      headerAlign: 'center',
      getActions: (params) => [
        <GridActionsCellItem color="primary" icon={<EditOutlinedIcon />} label="Edit" onClick={() => handleEdit(params.row.id)} />,
        <GridActionsCellItem color="secondary" icon={<DeleteOutlineOutlinedIcon />} label="Delete" onClick={() => handleDelete(params.row.id)} />,
      ],
    },
  ];

  return (
    <Box m="20px" >
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Header title="Category Type" module="category_type" subtitle="List of category types" />
      </Box>
      
      {/* Production Filter */}
      <Box sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 250 }}>
          <InputLabel id="prod-label">Παραγωγή</InputLabel>
          <Select
            labelId="prod-label"
            id="prod-id"
            label="Παραγωγή"
            value={selectedProduction?.id || ''}
            onChange={handleProductionChange}
          >
            <MenuItem value="">
              <em>Όλες οι Παραγωγές</em>
            </MenuItem>
            {productions?.map((production) => (
              <MenuItem key={production.id} value={production.id}>
                {production.p_descr}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={selectedCategoryType ? 8 : 12}>

          <FormListToolbar
            handleAdd={handleAdd}
            disableExport={true}
          />
          <Box
            m="8px 0 0 0"
            height="80vh"
          >
            <Paper elevation={2} style={{ padding: '1rem', height: '100%', width: '100%' }}>

              <SearchField searchQuery={searchQuery} handleSearchChange={handleSearchChange} handleClear={handleClear} ml={2} mr={2} />

              {isLoading && <LinearProgress />}

              {error && <ErrorNotification ml={2} mr={2} error={error} />}

              <TableContainer style={{ height: '87%', width: '100%' }}>
                <DataGrid
                  rows={flattenedData || []}
                  columns={columns}
                  getRowId={getRowId}
                  columnVisibilityModel={{
                    id: false,
                  }}
                  onRowClick={(params) => setSelectedCategoryType(params.row)}
                  sx={{
                    '& .MuiDataGrid-cell': {
                      borderRight: '1px solid #ddd',
                      borderBottom: 'none',
                    },
                    '& .MuiDataGrid-columnHeader': {
                      borderRight: '1px solid #ddd',
                    },
                    '& .MuiDataGrid-root': {
                      border: 'none',
                    },
                    '& .MuiDataGrid-columnsContainer': {
                      backgroundColor: '#f5f5f5',
                    },
                    '& .MuiDataGrid-footerContainer': {
                      borderTop: '1px solid #e0e0e0',
                    },
                    '& .MuiTablePagination-root': {
                      color: '#000',
                    },
                    '& .MuiDataGrid-columnHeaderTitle': {
                      fontWeight: 'bold',
                    },
                    '& .MuiDataGrid-row:hover': {
                      backgroundColor: '#f5f5f5',
                    },
                    '& .MuiDataGrid-cell:focus': {
                      outline: 'none',
                    },
                  }}
                />
              </TableContainer>
            </Paper>
          </Box>
        </Grid>
        {selectedCategoryType && (
          <Grid item xs={4}>
            <CategoryTypeDetails
              category_type={selectedCategoryType}
              onClose={handlePanelClose} />

          </Grid>
        )}
      </Grid>
    </Box>

  );
};

export default CategoryType;